import * as React from 'react';
import { FeatureUnavailableBanner, Loader, PageBody, PlanUpgradeBanner } from 'src/components';
import { isErrorResponseResult, planUpgradeRequired } from 'src/api/utils/errorHandlers';
import { useMigrationOverviewQuery } from 'src/api/queries/migrationQueries';
import { useFeatureAvailability } from 'src/hooks/useFeatureAvailability';
import { MigrationOverview } from 'src/types/TMigration';
import MovingSection from './MovingSection';
import ProviderChooser from './ProviderChooser';
import ProviderSection from './ProviderSection';
import ServingSection from './ServingSection';
import { Note, Pill } from './parts';
import styles from './styles.module.scss';

/**
 * Configure > Migrate to FileSpin — moving an account off Cloudinary or ImageKit.
 *
 * # Placement
 *
 * Its own Configure page, not a Settings tab. The Settings strip is "how
 * FileSpin itself behaves" and is admin+manager; this page absorbs ANOTHER
 * vendor's URLs, is a one-off project rather than steady-state tuning, and is
 * admin-only because registering a delivery id claims an identifier globally,
 * entering vendor credentials hands us a third-party secret, and starting a
 * copy writes assets into the library and bytes onto the invoice. It follows
 * the Analytics Integrations page: standalone route, admin + deployment-flag
 * gate, shared availability banners.
 *
 * # The shape, and why it changed
 *
 * The first version was six numbered steps, each a large card of prose with
 * form fields inside it, and it read as a manual rather than as settings. Three
 * things were wrong with it and all three are fixed here:
 *
 *  - It asked for the vendor account id **and** the delivery URL, which for a
 *    real customer are the same string. migration-onramp now reads the id out
 *    of the URL (`deriveIdentifier`), so the field is gone.
 *  - It presented "check my URLs" and "copy my library" as mandatory setup
 *    steps with their forms permanently open. They are optional tools, behind a
 *    button each.
 *  - It wrapped a whole section around a single toggle.
 *
 * What is left is a provider chooser for an account that has not started, and
 * three ordinary settings sections once it has — label left, control right,
 * one short line of help — matching the AI and Storage pages: **who you are
 * leaving**, **turning it on**, and **how the images are getting across**.
 *
 * # State
 *
 * One query for the whole page (`GET /api/v1/migration`), because the screen is
 * a state machine — not started, connected but unverified, verified, moving,
 * done — and assembling that from several responses makes it flicker through
 * wrong intermediate states on every load. Every write answers with the same
 * document, so nothing here patches its own copy of the truth.
 *
 * # Not gated by plan
 *
 * Deliberately. Pulled assets already count against storage and asset caps,
 * CDN bytes are billed, and ownership verification controls abuse; the buyer
 * this exists for is on a trial. If that changes, the API answers the
 * structured `plan_upgrade_required` 403 that every other gate uses, and the
 * branch below renders the shared banner - nothing else on this page moves.
 */

export const MigrationSettings: React.FC = () => {
  const { data, isLoading, error } = useMigrationOverviewQuery();
  const featureStatus = useFeatureAvailability('migration');

  const status = error && isErrorResponseResult(error) ? error.status : undefined;
  // 503 is not a failure: it means this deployment has no migration service, so
  // the page says so plainly rather than showing a form that cannot save. The
  // sidebar item greys out on the same probe.
  const unavailable = status === 503 || featureStatus === 'unavailable';
  const gate = error ? planUpgradeRequired(error) : null;
  const forbidden = status === 403 || status === 401;

  if (isLoading) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.loading}>
          <Loader />
        </div>
      </div>
    );
  }

  if (unavailable || forbidden || (error && !data)) {
    return (
      <div className={styles.pageWrapper}>
        <PageBody narrow>
          <Header />
          {unavailable ? (
            <FeatureUnavailableBanner message="Migration is not switched on for this FileSpin deployment yet. Your FileSpin contact can enable it." />
          ) : gate ? (
            <PlanUpgradeBanner gate={gate} />
          ) : (
            <Note tone="warn">
              {forbidden
                ? 'Only an account administrator can set up a migration.'
                : 'Could not load your migration settings. Please try again in a moment.'}
            </Note>
          )}
        </PageBody>
      </div>
    );
  }

  const overview = data as MigrationOverview;

  // Not started: the only decision on the page is which provider. Everything
  // else appears once that is made, and "Clear this set-up" brings it back.
  if (!overview.provider) {
    return (
      <div className={styles.pageWrapper}>
        <PageBody narrow>
          <Header overview={overview} />
          <ProviderChooser />
        </PageBody>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <PageBody narrow>
        <Header overview={overview} />
        <ProviderSection overview={overview} />
        <ServingSection overview={overview} />
        <MovingSection overview={overview} />
      </PageBody>
    </div>
  );
};

/** The promise in one sentence, and where this account has got to.
 *
 *  "Your URLs do not change" is what decides whether the rest of the page is
 *  worth reading, so it is the only prose above the fold. */
const Header: React.FC<{ overview?: MigrationOverview }> = ({ overview }) => {
  const live = overview?.keys.some((k) => k.resolvable);
  const moving = (overview?.assets.aliased || 0) > 0;

  let pill = <Pill>Not set up</Pill>;
  if (live && moving) pill = <Pill tone="good">Moving across</Pill>;
  else if (live) pill = <Pill tone="good">Ready</Pill>;
  else if (overview?.enrolled) pill = <Pill tone="warn">Almost there</Pill>;

  return (
    <header className={styles.pageHeader}>
      <div>
        <h1 className={styles.pageTitle}>Migrate from another provider</h1>
        <p className={styles.pageLead}>
          Move off Cloudinary or ImageKit without editing a single URL. Your existing image links keep
          working — we read your old provider&rsquo;s settings and copy each image across the first time
          somebody asks for it.
        </p>
      </div>
      <div className={styles.pageStatus}>{pill}</div>
    </header>
  );
};

export default MigrationSettings;
